import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import Reveal from "@/components/Reveal";
import ModelCard from "@/components/ModelCard";
import SpecModal from "@/components/SpecModal";

const BRAND_TABS = [
  { key: "", label: "All" },
  { key: "honda", label: "Honda" },
  { key: "proton", label: "Proton" },
  { key: "jaecoo", label: "Jaecoo" },
  { key: "omoda", label: "Omoda" },
];
const BODY_TABS = ["All", "Sedan", "SUV", "EV"];
const PRICE_TABS = [
  { key: "", label: "Any price" },
  { key: "low", label: "Under RM80k", test: (p) => p < 80000 },
  { key: "mid", label: "RM80k–130k", test: (p) => p >= 80000 && p <= 130000 },
  { key: "high", label: "RM130k+", test: (p) => p > 130000 },
];

export default function Models() {
  const [params, setParams] = useSearchParams();
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [body, setBody] = useState("All");
  const [price, setPrice] = useState("");
  const brand = params.get("brand") || "";

  useEffect(() => {
    document.title = "All Models — AutoWorld";
    api.models().then((d) => { setModels(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let out = models;
    if (brand) out = out.filter((m) => m.brand === brand);
    if (body !== "All") out = out.filter((m) => m.body_type === body);
    const pt = PRICE_TABS.find((t) => t.key === price);
    if (pt?.test) out = out.filter((m) => pt.test(m.price_from));
    return out;
  }, [models, brand, body, price]);

  const chip = (on) =>
    `px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] border transition-colors ${
      on ? "bg-black text-white border-black" : "border-black/20 hover:border-black"
    }`;

  return (
    <div className="pt-16 md:pt-20" data-testid="models-page">
      <section className="border-b border-black/10">
        <div className="mx-auto max-w-[1400px] px-5 py-14 md:px-10 md:py-20">
          <Reveal>
            <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-neutral-500">Every model · Direct price</div>
            <h1 className="mt-4 font-display text-5xl font-extrabold uppercase tracking-tighter md:text-7xl">The Lineup</h1>
            <p className="mt-4 max-w-md text-neutral-600">
              Honda, Proton, Jaecoo &amp; Omoda — full specs, transparent pricing, zero agent markup.
            </p>
          </Reveal>
        </div>
      </section>

      <div className="sticky top-16 z-30 border-b border-black/10 bg-white/90 backdrop-blur-xl md:top-20" data-testid="filter-bar">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-2 px-5 py-4 md:px-10">
          {BRAND_TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`filter-brand-${t.key || "all"}`}
              className={chip(brand === t.key)}
              onClick={() => setParams(t.key ? { brand: t.key } : {})}
            >
              {t.label}
            </button>
          ))}
          <span className="mx-2 hidden h-6 w-px bg-black/15 md:block" />
          {BODY_TABS.map((b) => (
            <button key={b} data-testid={`filter-body-${b.toLowerCase()}`} className={chip(body === b)} onClick={() => setBody(b)}>
              {b}
            </button>
          ))}
          <span className="mx-2 hidden h-6 w-px bg-black/15 md:block" />
          {PRICE_TABS.map((p) => (
            <button key={p.key} data-testid={`filter-price-${p.key || "any"}`} className={chip(price === p.key)} onClick={() => setPrice(p.key)}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <section className="mx-auto max-w-[1400px] px-5 py-14 md:px-10 md:py-20">
        <div className="mb-8 font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500" data-testid="models-count">
          {loading ? "Loading…" : `${filtered.length} model${filtered.length === 1 ? "" : "s"} · prices direct from dealer`}
        </div>
        {filtered.length === 0 && !loading ? (
          <div className="border border-black/10 p-16 text-center" data-testid="models-empty">
            <p className="font-display text-2xl font-bold uppercase">No models match</p>
            <p className="mt-2 text-sm text-neutral-500">Try widening your filters — or WhatsApp us, we'll find it.</p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="models-grid">
            {filtered.map((m, i) => (
              <ModelCard key={m.id} model={m} index={i} onView={setActive} />
            ))}
          </div>
        )}
      </section>

      <SpecModal model={active} onClose={() => setActive(null)} />
    </div>
  );
}
